import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { AuthPermissions } from 'skipify-types';
import { useMediaQuery } from '@mui/system';

import { cardOnFilePaths } from '../../util/constants';
import { MerchantLogo } from '../Paylinks/MerchantLogo';
import { useTokenRemoval } from '../../hooks';
import appTheme from '../../styles/themes/theme';
import { SubTabBar } from '../../common/SubTabBar';
import { MobileSubTabBar } from '../../common/SubTabBar/MobileSubTabBar';
import { useMerchant } from '../../contexts/MerchantContext';
import { useGetPublicMerchantQuery } from '../../store/modules/merchant/api';
import { ApplicationLoading } from '../../common';
import { useMerchantAwareNavigate } from '../../hooks/useMerchantAwareNavigate';

const TABS = [
  {
    label: 'Create transaction',
    id: 'tab-create-tof-transaction',
    path: cardOnFilePaths.CREATE_TRANSACTION,
  },
  {
    label: 'Transactions',
    id: 'tab-view-all-tof-transactions',
    path: cardOnFilePaths.VIEW_ALL,
  },
  {
    label: 'Customers',
    id: 'tab-cof-customers',
    path: cardOnFilePaths.CUSTOMERS,
  },
];

export const CardOnFileAppShell = () => {
  const {
    merchantId,
    merchantName: name,
    isLoading: merchantInfoLoading,
    canAccess,
  } = useMerchant();
  const { data: publicData, isLoading: isLoadingPublicMerchant } = useGetPublicMerchantQuery(
    merchantId,
    { skip: !merchantId },
  );
  const [selectedTab, setSelectedTab] = useState(0);
  const merchantName = publicData?.branding?.displayName ?? name;
  const location = useLocation();
  const navigate = useMerchantAwareNavigate();
  const isMobile = useMediaQuery(appTheme.breakpoints.down('sm'));

  useTokenRemoval();

  // Hook-based permission checks
  const canChargeOwnMit = canAccess(AuthPermissions.CHARGE_OWN_MIT);
  const canChargeMit = canAccess(AuthPermissions.CHARGE_MIT);
  const canReadMit = canAccess(AuthPermissions.READ_MIT);
  const canReadOwnMit = canAccess(AuthPermissions.READ_OWN_MIT);
  const canReadPaylinkCustomers = canAccess(AuthPermissions.READ_PAYLINK_CUSTOMERS);
  const canReadOwnPaylinkCustomers = canAccess(AuthPermissions.READ_OWN_PAYLINK_CUSTOMERS);

  const CARD_ON_FILE_TABS = useMemo(() => {
    const tabs: (typeof TABS)[number][] = [];

    if (canChargeOwnMit || canChargeMit) {
      tabs.push(TABS[0]);
    }
    if (canReadMit || canReadOwnMit) {
      tabs.push(TABS[1]);
    }
    if (canReadPaylinkCustomers || canReadOwnPaylinkCustomers) {
      tabs.push(TABS[2]);
    }

    return tabs;
  }, [
    canChargeOwnMit,
    canChargeMit,
    canReadMit,
    canReadOwnMit,
    canReadPaylinkCustomers,
    canReadOwnPaylinkCustomers,
  ]);

  useEffect(() => {
    if (CARD_ON_FILE_TABS.length === 0) {
      return;
    }

    const matchingPathIndex = CARD_ON_FILE_TABS.findIndex(
      (tab) => tab.path.toLowerCase() === location.pathname.toLowerCase(),
    );

    if (matchingPathIndex === -1) {
      navigate(CARD_ON_FILE_TABS[0].path);
      setSelectedTab(0);
      return;
    }

    if (selectedTab === matchingPathIndex) {
      return;
    }

    setSelectedTab(matchingPathIndex);
  }, [location.pathname, selectedTab, CARD_ON_FILE_TABS, navigate]);

  if (!merchantName || merchantInfoLoading || isLoadingPublicMerchant) {
    return <ApplicationLoading />;
  }

  const TabBar = isMobile ? MobileSubTabBar : SubTabBar;

  return (
    <Box
      component="main"
      data-testid="card-on-file-app-shell"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        flexGrow: 1,
        pt: { xs: 0, sm: 2, md: 6 },
        px: { xs: 2, sm: 2, md: 6.5 },
        pb: 0,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '1400px' }}>
        {!isMobile && (
          <MerchantLogo
            logoSrc={publicData?.branding?.logoSrc}
            fallbackMerchantName={merchantName}
          />
        )}
        <Box
          sx={{
            marginTop: { xs: 4, sm: 4, md: 5 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TabBar
            tabs={CARD_ON_FILE_TABS}
            selectedTab={selectedTab}
            setSelectedTab={(tabIndex, tabContent) => {
              setSelectedTab(tabIndex);
              navigate(tabContent.path);
            }}
            ariaLabel="Card on file Tabs"
          />
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};
